import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle2, FileText, Map, Settings, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { useCampaign } from '@/api/campaigns/use-campaigns';
import { CampaignStepper, type CampaignStepDefinition } from './CampaignStepper';
import SourceStep from './steps/SourceStep';
import FieldsStep from './steps/FieldsStep';
import MappingStep from './steps/MappingStep';
import DefaultsStep from './steps/DefaultsStep';
import ReviewStep from './steps/ReviewStep';

export interface CampaignEditorProps {
    mode: 'create' | 'edit';
    campaignId?: string;
}

function clampStep(step: number): number {
    if (Number.isNaN(step)) return 1;
    return Math.min(5, Math.max(1, step));
}

/**
 * Orchestrator for the campaign create/edit flow (quantix-api/docs/campaigns/TASKS.md,
 * task W3): a 5-step wizard — source, fields, mapping, defaults, review — shared
 * between `/campaigns/new` and `/campaigns/:campaignId/edit`.
 *
 * In create mode only step 1 is interactive: `POST /campaigns` creates the
 * draft (and links the Facebook form, if any), then navigates to
 * `/campaigns/:id/edit?step=2` where this same component continues in edit
 * mode. In edit mode every step saves to the API on "Continue" and the
 * current step is reflected in the `?step=` query param so it survives a
 * refresh.
 */
const CampaignEditor: React.FC<CampaignEditorProps> = ({ mode, campaignId }) => {
    const { t } = useTranslation('crm');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const steps: CampaignStepDefinition[] = useMemo(
        () => [
            { id: 1, label: t('campaigns.editor.steps.source'), icon: Target },
            { id: 2, label: t('campaigns.editor.steps.fields'), icon: FileText },
            { id: 3, label: t('campaigns.editor.steps.mapping'), icon: Map },
            { id: 4, label: t('campaigns.editor.steps.defaults'), icon: Settings },
            { id: 5, label: t('campaigns.editor.steps.review'), icon: CheckCircle2 },
        ],
        [t]
    );

    const [currentStep, setCurrentStep] = useState(() =>
        clampStep(Number(searchParams.get('step')))
    );

    const goToStep = (step: number) => {
        const clamped = clampStep(step);
        setCurrentStep(clamped);
        const next = new URLSearchParams(searchParams);
        next.set('step', String(clamped));
        setSearchParams(next, { replace: true });
    };

    const { data, isLoading, refetch } = useCampaign(campaignId ?? '');
    const campaign = mode === 'edit' ? (data?.data ?? null) : null;

    if (mode === 'create') {
        return (
            <PageContainer>
                <PageHeader
                    title={t('campaigns.editor.createTitle')}
                    actions={
                        <Button variant="outline" onClick={() => navigate('/campaigns')}>
                            <ArrowLeft className="h-4 w-4" />
                            {t('actions.back')}
                        </Button>
                    }
                />
                <CampaignStepper steps={steps} currentStep={1} furthestStep={1} />
                <SourceStep
                    mode="create"
                    campaign={null}
                    onCreated={id => navigate(`/campaigns/${id}/edit?step=2`)}
                    onSavedAndAdvance={() => {}}
                />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader
                title={campaign?.name ?? t('campaigns.editor.editTitle')}
                actions={
                    <Button
                        variant="outline"
                        onClick={() =>
                            navigate(campaignId ? `/campaigns/${campaignId}` : '/campaigns')
                        }
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {t('actions.back')}
                    </Button>
                }
            />

            <CampaignStepper
                steps={steps}
                currentStep={currentStep}
                furthestStep={campaign ? 5 : currentStep}
                onStepClick={campaign ? goToStep : undefined}
            />

            {isLoading || !campaign ? (
                <div className="space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-48 w-full" />
                </div>
            ) : (
                <>
                    {currentStep === 1 && (
                        <SourceStep
                            mode="edit"
                            campaign={campaign}
                            onCreated={() => {}}
                            onSavedAndAdvance={() => goToStep(2)}
                        />
                    )}
                    {currentStep === 2 && (
                        <FieldsStep
                            campaign={campaign}
                            onRefetchCampaign={() => void refetch()}
                            onBack={() => goToStep(1)}
                            onAdvance={() => goToStep(3)}
                        />
                    )}
                    {currentStep === 3 && (
                        <MappingStep
                            campaign={campaign}
                            onBack={() => goToStep(2)}
                            onAdvance={() => goToStep(4)}
                        />
                    )}
                    {currentStep === 4 && (
                        <DefaultsStep
                            campaign={campaign}
                            onBack={() => goToStep(3)}
                            onAdvance={() => goToStep(5)}
                        />
                    )}
                    {currentStep === 5 && (
                        <ReviewStep campaign={campaign} onBack={() => goToStep(4)} />
                    )}
                </>
            )}
        </PageContainer>
    );
};

export default CampaignEditor;
